import { ReactElement } from "react";
import sanitizeHtml from "sanitize-html";
import cx from "apps/website/utils/misc/cx";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";

interface IHighlightTextWithHTML {
  text: string;
  theme?: Theme;
  display?: "inline" | "inline-block";
  rotate?: boolean;
}

/**
 * Highlights text wrapped in backticks.
 * @param legacy - Default is true (returns HTML string for backward compatibility).
 * Set to false to return React elements (preferred for new code).
 * @example {highlightTextWithHTML({ text: "`highlighted`" })} // returns HTML string
 * @example {highlightTextWithHTML({ text: "`highlighted`", legacy: false })} // React elements
 */
function highlightTextWithHTML(
  options: IHighlightTextWithHTML & { legacy: true },
): string;

function highlightTextWithHTML(
  options: IHighlightTextWithHTML & { legacy: false },
): ReactElement[];

function highlightTextWithHTML(
  options: IHighlightTextWithHTML & { legacy?: boolean },
): string | ReactElement[];

function highlightTextWithHTML({
  text,
  theme = "brand",
  display = "inline",
  rotate = false,
  legacy = true,
}: IHighlightTextWithHTML & { legacy?: boolean }): string | ReactElement[] {
  const sanitizedText = sanitizeHtml(text);
  const highlightIndicator = "`";
  const splitText = sanitizedText.split(highlightIndicator);

  const highlightClass = cx(
    themeRootClassMap[theme],
    display,
    "relative z-0",
    rotate && display === "inline-block" && "-rotate-1",
  );

  if (legacy) {
    return splitText
      .map((segment, index) => {
        if (index % 2 === 0) {
          return `<span class="relative z-10">${segment}</span>`;
        }

        return `<span class='${highlightClass}'><span class="relative z-10">${segment}</span></span>`;
      })
      .join("");
  }

  return splitText.map((segment, index) => {
    const key = `segment-${index}`;

    if (index % 2 === 0) {
      return <span key={ key } className="relative z-10">{ segment }</span>;
    }

    return (
      <span key={ key } className={ highlightClass }>
        <span className="relative z-10">{ segment }</span>
      </span>
    );
  });
}

export default highlightTextWithHTML;
